import { useEffect, useState } from "react";
import { BudgetingType } from "../../BudgetingType";
import { useConfig } from "../../hooks/useConfig";
import { useExportPropertyBudgetLazyQuery, useExportPropertyBudgetT12ReportLazyQuery, useGetBudgetSeasonExportDataLazyQuery, useGetUnitsCountLazyQuery, VersionType } from "../../__generated__/generated_types";
import useYearDefinitions from "../annualsummary/YearDefinitions";
import { exportArbitraryMonthRangeCsv, exportCsv } from "./csv/exporters";
import { exportExcel, exportArbitraryMonthRangeExcel } from "./excel/exporters";
import { useExportT12ReportHook } from "./excel/exportT12ReportHook";

export enum BudgetExportFormat {
    CSV_REPORT = "CSV_REPORT",
    CSV_YARDI = "CSV_YARDI",
    CSV_YARDI_1_cent = "CSV_YARDI_1_CENT",
    CSV_ARBITRARY_MONTH_RANGE = "CSV_ARBITRARY_MONTH_RANGE",
    CSV_APPFOLIO = "APPFOLIO",
    EXCEL_FULL_NO_ZEROS = "EXCEL_FULL_NO_ZEROS",
    EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET = "EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET",
    EXCEL_THREE_WAY_COMPARISON = "EXCEL_THREE_WAY_COMPARISON",
    EXCEL_FULL_NO_ZEROS_WITH_METRICS = "EXCEL_FULL_NO_ZEROS_WITH_METRICS",
    EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS = "EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS",
    EXCEL_T12_REPORT = "EXCEL_T12_REPORT",
    EXCEL_ARBITRARY_MONTH_RANGE = "EXCEL_ARBITRARY_MONTH_RANGE",
}

export default function useBudgetExportData(tab: BudgetingType, budgetYear: number) {
    const config = useConfig();
    const { currentDefinition: referenceYear } = useYearDefinitions();
    const exportT12Report = useExportT12ReportHook();

    const [exportState, setExportState] = useState<{ isReadyToExport: boolean, exportFormat: BudgetExportFormat }>({ isReadyToExport: false, exportFormat: BudgetExportFormat.CSV_REPORT });

    const [loadData, { data, loading }] = useExportPropertyBudgetLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [getUnitsCount, { data: unitsCountData, loading: unitsCountLoading}] = useGetUnitsCountLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [loadT12ReportData, { data: dataT12Report, loading: loadingT12Report }] = useExportPropertyBudgetT12ReportLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [loadBudgetSeasonData, { data: dataBudgetSeason, loading: loadingBudgetSeason }] = useGetBudgetSeasonExportDataLazyQuery({
        fetchPolicy: "no-cache"
    });


    function buildExtraReferenceYear(exportFormat: BudgetExportFormat = BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET) {
        let extraReferenceYear = {
            referenceYear: config.year,
            referenceValueType: VersionType.Budget
        };
        if (tab == BudgetingType.BUDGET && exportFormat === BudgetExportFormat.EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS) {
            extraReferenceYear = {
                referenceYear: config.year,
                referenceValueType: VersionType.YearTotal // TODO: kill year total!
            };
        }
        if (tab == BudgetingType.REFORECAST && exportFormat === BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON) {
            extraReferenceYear = {
                referenceYear: config.year - 1,
                referenceValueType: VersionType.Actuals
            };
        }
        return extraReferenceYear;
    }

    function buildEffectiveReferenceYear(exportFormat: BudgetExportFormat) {
        if (exportFormat === BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON) {
            return {
                ...referenceYear,
                year: config.year,
                type: tab == BudgetingType.BUDGET ? VersionType.YearTotal : VersionType.Budget
            };
        }
        return referenceYear;
    }

    useEffect(
        () => {
            if (!loading && config.isReady) {
                setExportState(
                    prev =>
                    ({
                        ...prev,
                        isReadyToExport: true,
                    })
                );
            }
        },
        [config.isReady]
    );

    const exportBudget = (exportFormat: BudgetExportFormat) => {
        if (!config.isReady) {
            return;
        }
        setExportState(
            prev =>
            ({
                ...prev,
                isReadyToExport: false,
                exportFormat: exportFormat
            })
        );

        if (exportFormat == BudgetExportFormat.EXCEL_T12_REPORT) {
            loadT12ReportData({
                variables: {
                    propertyId: config.properties.propertyId,
                    budgetYear: budgetYear
                }
            });
        }
        else if (exportFormat == BudgetExportFormat.CSV_ARBITRARY_MONTH_RANGE || exportFormat == BudgetExportFormat.EXCEL_ARBITRARY_MONTH_RANGE) {
            loadBudgetSeasonData({
                variables: {
                    propertyId: config.properties.propertyId,
                    budgetYear: budgetYear,
                    chartOfAccountsId: config.chartOfAccountsConfig.chartOfAccountsId ?? ""
                }
            });
        }
        else {
            let extraReferenceYear = {
                referenceYear: referenceYear.year,
                referenceValueType: referenceYear.type
            };
            let includeExtraReferenceYear = false;

            if (exportFormat == BudgetExportFormat.EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS) {
                includeExtraReferenceYear = true;
                extraReferenceYear = buildExtraReferenceYear(BudgetExportFormat.EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS);
            }
            else if (exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET) {
                includeExtraReferenceYear = true;
                extraReferenceYear = buildExtraReferenceYear(BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET);

            }
            else if (exportFormat == BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON) {
                includeExtraReferenceYear = true;
                extraReferenceYear = buildExtraReferenceYear(BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON);
            }
            const effectiveReferenceYear = buildEffectiveReferenceYear(exportFormat);
            loadData({
                variables: {
                    propertyId: config.properties.propertyId,
                    requestValueType: tab == BudgetingType.REFORECAST ? VersionType.Reforecast : VersionType.Budget,
                    referenceYear: effectiveReferenceYear.year,
                    referenceValueType: effectiveReferenceYear.type,
                    extraReferenceYear: extraReferenceYear.referenceYear,
                    extraReferenceValueType: extraReferenceYear.referenceValueType,
                    includeExtraReferenceYear: includeExtraReferenceYear,
                    budgetYear: budgetYear
                }
            });
            getUnitsCount({variables: {propertyId: config.properties.propertyId, budgetYear: budgetYear}});
        }
    };

    useEffect(
        () => {
            if (data && !loading && unitsCountData && !unitsCountLoading) {
                if (exportState.exportFormat == BudgetExportFormat.CSV_REPORT
                    || exportState.exportFormat == BudgetExportFormat.CSV_APPFOLIO
                    || exportState.exportFormat == BudgetExportFormat.CSV_YARDI
                    || exportState.exportFormat == BudgetExportFormat.CSV_YARDI_1_cent
                ) {
                    exportCsv(
                        exportState.exportFormat,
                        tab,
                        referenceYear,
                        data,
                        config
                    );
                }
                if (exportState.exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS
                    || exportState.exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET
                    || exportState.exportFormat == BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON
                    || exportState.exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_METRICS
                    || exportState.exportFormat == BudgetExportFormat.EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS
                ) {
                    const extraReferenceYear = buildExtraReferenceYear(exportState.exportFormat);
                    const effectiveReferenceYear = buildEffectiveReferenceYear(exportState.exportFormat);
                    exportExcel(
                        exportState.exportFormat,
                        tab,
                        effectiveReferenceYear,
                        extraReferenceYear,
                        data,
                        config,
                        unitsCountData.propertyUnitCounts.map(row => row.count).sum(),
                        unitsCountData.propertyUnitCounts.map(row => row.sqft).sum()
                    );
                }
                setExportState(
                    prev =>
                    ({
                        ...prev,
                        isReadyToExport: true,
                    })
                );
            }
        },
        [data, loading, unitsCountData, unitsCountLoading]
    );

    useEffect(
        () => {
            if (dataT12Report && !loadingT12Report) {
                exportT12Report(dataT12Report, config);
                setExportState(
                    prev =>
                    ({
                        ...prev,
                        isReadyToExport: true,
                    })
                );
            }
        },
        [dataT12Report, loadingT12Report]
    );

    useEffect(
        () => {
            if (dataBudgetSeason && !loadingBudgetSeason) {
                const property = config.properties.currentProperty;
                if(!property) {
                    return;
                }
                if (exportState.exportFormat == BudgetExportFormat.CSV_ARBITRARY_MONTH_RANGE) {
                    exportArbitraryMonthRangeCsv({
                        data: dataBudgetSeason,
                        startMonth: property.reforecastStartMonthIndex,
                        months: 12,
                        property: property,
                        budgetYear: budgetYear
                    });
                }
                else if (exportState.exportFormat == BudgetExportFormat.EXCEL_ARBITRARY_MONTH_RANGE) {
                    exportArbitraryMonthRangeExcel({
                        data: dataBudgetSeason,
                        startMonth: property.reforecastStartMonthIndex,
                        months: 12,
                        property: property,
                        budgetYear: budgetYear
                    });
                }
                setExportState(
                    prev =>
                    ({
                        ...prev,
                        isReadyToExport: true,
                    })
                );
            }
        },
        [dataBudgetSeason, loadingBudgetSeason]
    );

    return {
        isReadyToExport: exportState,
        exportBudget: exportBudget,
        reforecastYear: config.year
    };
}